import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { friendlyError } from "../../lib/friendlyError";
import { trpc } from "../../trpc";

import {
  canConfirmPlace,
  confirmationNotSent,
  paymentStatusLabel,
} from "./guestFormat";
import type { RosterRow } from "./shared";

/** A second tap of an ordinary double-tap lands in the next step's
 *  button; ignore taps this soon after the step appears. */
const CONFIRM_DEAD_TIME_MS = 400;

const OUTLINE_BUTTON =
  "inline-flex min-h-11 items-center justify-center gap-1.5 rounded-md border border-[color:var(--color-line-strong)] px-3.5 text-[14px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)] disabled:opacity-50";

const DONE_LINE =
  "inline-flex items-center gap-1.5 text-[14px] font-medium text-[color:var(--color-success)]";

type ConfirmMode = "gifted" | "cleared";

/**
 * Confirm a hand-added place: the bank transfer arrived, or the place is
 * a gift. Choosing an option only asks; the email goes when the second
 * step is confirmed, because a gift also records the booking as no charge.
 */
function ConfirmPlace({
  id,
  name,
  email,
  methodUnknown,
}: {
  id: string;
  name: string;
  email: string;
  /** The booking has no recorded payment method, so it may be a card
   *  payment whose receipt never arrived. */
  methodUnknown: boolean;
}) {
  const utils = trpc.useUtils();
  // Refresh the list here, not per call, so it still happens if Details
  // is gone by the time the email has been sent.
  const confirm = trpc.adminSendPlaceConfirmed.useMutation({
    onSuccess: () => {
      void utils.adminListEventRegistrations.invalidate();
    },
  });
  const [step, setStep] = useState<"closed" | "choose" | ConfirmMode>("closed");
  const [ready, setReady] = useState(false);
  const [done, setDone] = useState<ConfirmMode | null>(null);
  const confirmPlaceRef = useRef<HTMLButtonElement>(null);
  const firstChoiceRef = useRef<HTMLButtonElement>(null);
  const goBackRef = useRef<HTMLButtonElement>(null);
  const shownStep = useRef(step);

  // Each step replaces the button just pressed, so move focus to the new
  // step: a screen reader hears it, and the keyboard keeps its place.
  // Only on a change, so rows never take focus when the page loads.
  useEffect(() => {
    if (shownStep.current === step) {
      return;
    }
    shownStep.current = step;
    if (step === "closed") {
      confirmPlaceRef.current?.focus();
    } else if (step === "choose") {
      firstChoiceRef.current?.focus();
    } else {
      goBackRef.current?.focus();
    }
  }, [step]);

  useEffect(() => {
    if (step !== "gifted" && step !== "cleared") {
      return;
    }
    const timer = window.setTimeout(() => setReady(true), CONFIRM_DEAD_TIME_MS);
    return () => window.clearTimeout(timer);
  }, [step]);

  function ask(mode: ConfirmMode) {
    setReady(false);
    setStep(mode);
  }

  function send(mode: ConfirmMode) {
    if (!ready) {
      return;
    }
    confirm.mutate(
      { id, mode },
      {
        onSuccess: () => {
          setDone(mode);
          setStep("closed");
        },
      },
    );
  }

  if (done) {
    return (
      <p role="status" className={DONE_LINE}>
        <Check size={16} aria-hidden="true" />
        {done === "gifted"
          ? "Gifted place confirmed. Email sent."
          : "Place confirmed. Email sent."}
      </p>
    );
  }

  const error = confirm.error ? (
    <p role="alert" className="text-[14px] text-[color:var(--color-danger)]">
      {friendlyError(confirm.error)}
    </p>
  ) : null;

  if (step === "closed") {
    return (
      <div className="flex flex-col items-start gap-1">
        <button
          ref={confirmPlaceRef}
          type="button"
          onClick={() => setStep("choose")}
          className={OUTLINE_BUTTON}
        >
          Confirm place
        </button>
        {error}
      </div>
    );
  }

  if (step === "choose") {
    return (
      <div
        role="group"
        aria-label={`Confirm ${name}'s place`}
        className="flex flex-col items-start gap-2"
      >
        <p className="text-[14px] text-[color:var(--color-ink-soft)]">
          How was this place covered?
        </p>
        {methodUnknown ? (
          <p className="text-[14px] text-[color:var(--color-ink-soft)]">
            Only for places added by hand. If they paid by card, don&apos;t use
            this.
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <button
            ref={firstChoiceRef}
            type="button"
            onClick={() => ask("cleared")}
            className={OUTLINE_BUTTON}
          >
            Bank transfer received
          </button>
          <button
            type="button"
            onClick={() => ask("gifted")}
            className={OUTLINE_BUTTON}
          >
            Gifted, no charge
          </button>
          <button
            type="button"
            onClick={() => setStep("closed")}
            className="inline-flex min-h-11 items-center px-2 text-[14px] font-medium text-[color:var(--color-ink-soft)] underline underline-offset-4 hover:text-[color:var(--color-ink)]"
          >
            Cancel
          </button>
        </div>
        {error}
      </div>
    );
  }

  const mode = step;
  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-[14px] text-[color:var(--color-ink)]">
        Email {name} at {email}?
        {mode === "gifted"
          ? " This also records the booking as a gifted place, with no charge."
          : " This confirms their place as paid by bank transfer."}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => send(mode)}
          disabled={confirm.isPending}
          aria-disabled={!ready}
          className={`inline-flex min-h-11 items-center rounded-md bg-[color:var(--color-amber)] px-4 text-[14px] font-semibold text-[color:var(--color-bg)] transition-opacity hover:opacity-90 disabled:opacity-50 ${
            ready ? "" : "opacity-60"
          }`}
        >
          {confirm.isPending ? "Sending…" : "Send email"}
        </button>
        <button
          ref={goBackRef}
          type="button"
          onClick={() => setStep("choose")}
          disabled={confirm.isPending}
          className={OUTLINE_BUTTON}
        >
          Go back
        </button>
      </div>
      {error}
    </div>
  );
}

/** Send this booking's confirmation email: again, when the first may have
 *  gone to spam, or for the first time, when the booking was added without
 *  an email and one has been added since. */
function ResendConfirmation({
  id,
  neverSent,
}: {
  id: string;
  /** No confirmation has gone yet, so the button says Send, not Resend. */
  neverSent: boolean;
}) {
  const utils = trpc.useUtils();
  const resend = trpc.adminResendBookingConfirmation.useMutation({
    onSuccess: () => {
      void utils.adminListEventRegistrations.invalidate();
    },
  });
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <p role="status" className={DONE_LINE}>
        <Check size={16} aria-hidden="true" />
        Email sent
      </p>
    );
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={() =>
          resend.mutate(
            { id },
            {
              onSuccess: () => {
                setSent(true);
              },
            },
          )
        }
        disabled={resend.isPending}
        className={OUTLINE_BUTTON}
      >
        {resend.isPending
          ? "Sending…"
          : neverSent
            ? "Send confirmation"
            : "Resend confirmation"}
      </button>
      {resend.error ? (
        <p
          role="alert"
          className="text-[14px] text-[color:var(--color-danger)]"
        >
          {friendlyError(resend.error)}
        </p>
      ) : null}
    </div>
  );
}

/** The guest list's payment column: one short status ("Paid $500",
 *  "Gifted", "Not paid yet"), and a warning when the confirmation
 *  email hasn't gone. Nothing for a free booking (All events mixes them
 *  in with paid ones). */
export function PaymentStatus({ r }: { r: RosterRow }) {
  if (r.bookingType == null) {
    return null;
  }
  const label = paymentStatusLabel(r);
  let status;
  if (r.bookingType === "paid" && r.paymentMethod === "discount") {
    status = (
      <span className="badge border border-[color:var(--color-amber)]/45 text-[color:var(--color-amber)]">
        {label}
      </span>
    );
  } else if (r.bookingType === "paid" && r.paymentMethod !== "gifted") {
    status = (
      <span className="font-semibold text-[color:var(--color-ink)] tabular-nums">
        {label}
      </span>
    );
  } else {
    status = (
      <span className="font-semibold text-[color:var(--color-amber)]">
        {label}
      </span>
    );
  }
  return (
    <div className="flex flex-col items-start gap-0.5 text-[14px]">
      {status}
      {confirmationNotSent(r) ? (
        <span className="font-medium text-[color:var(--color-amber)]">
          Confirmation not sent yet
        </span>
      ) : null}
    </div>
  );
}

/** The emails the desk can send for one paid guest, under Details:
 *  Resend confirmation, and Confirm place for a place added by hand. */
export function GuestEmailActions({ r }: { r: RosterRow }) {
  if (r.bookingType !== "paid" || r.isWalkIn) {
    return null;
  }
  const email = r.email.trim();
  if (!email) {
    return (
      <p className="text-[14px] text-[color:var(--color-ink-soft)]">
        No email on file
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-start gap-3">
      <ResendConfirmation id={r.id} neverSent={confirmationNotSent(r)} />
      {canConfirmPlace(r) ? (
        <ConfirmPlace
          id={r.id}
          name={r.name}
          email={email}
          methodUnknown={r.paymentMethod === null}
        />
      ) : null}
    </div>
  );
}
